import React, { useState, useRef, useEffect } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  BarChart,
  BusinessCenterOutlined,
  ChevronRight,
  ExitToApp,
  FactCheckOutlined,
  KeyboardArrowDown,
  Person,
  StorageOutlined,
} from "@mui/icons-material";
import vyontaLogoInk from '../../assets/Vyonta-logo-ink.png';

const colors = {
  ink: "#06185f",
  muted: "#4c5d86",
  blue: "#0059f6",
  line: "#d9e3f4",
};

const shakeKeyframes = {
  "@keyframes shake": {
    "0%, 100%": { transform: "translateX(0)" },
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
  },
};

const modules = [
  {
    key: "ventas",
    title: "Ventas",
    description: "Gestioná oportunidades, cotizaciones y ventas.",
    icon: BarChart,
    protected: false,
  },
  {
    key: "admin",
    title: "Administración",
    description: "Administrá clientes, proveedores y procesos.",
    icon: BusinessCenterOutlined,
    protected: true,
  },
  {
    key: "postventa",
    title: "Posventa",
    description: "Seguimiento de entregas, fichas y estado de clientes.",
    icon: FactCheckOutlined,
    protected: true,
  },
  {
    key: "datos",
    title: "Datos",
    description: "Explorá reportes, indicadores y análisis.",
    icon: StorageOutlined,
    protected: true,
  },
];

const getDisplayName = (user) => (
  user?.nombre
  || user?.empresa_nombre
  || user?.empresa
  || "Empresa"
);

const PinPanel = ({ visible, onSubmit, externalError, onClearError }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setPin("");
      setError(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [visible]);

  useEffect(() => {
    if (externalError) {
      setError(true);

      const timer = setTimeout(() => {
        setPin("");
        setError(false);
        onClearError?.();
        setTimeout(() => inputRef.current?.focus(), 40);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [externalError, onClearError]);

  const handleChange = (e) => {
    if (error) return;

    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
    setPin(val);

    if (val.length === 4) onSubmit(val);
  };

  const renderPinText = () => {
    if (error) return "INCORRECTO";

    const filled = "X ".repeat(pin.length);
    const empty = "- ".repeat(4 - pin.length);
    return `${filled}${empty}`.trim();
  };

  return (
    <Collapse
      in={visible}
      timeout={260}
      unmountOnExit
      sx={{
        width: "100%",
        "& .MuiCollapse-wrapperInner": {
          display: "flex",
          justifyContent: "center",
        },
      }}
    >
      <Paper
        elevation={0}
        onClick={() => inputRef.current?.focus()}
        sx={{
          ...shakeKeyframes,
          width: "calc(100% - 0px)",
          minHeight: 58,
          px: { xs: 2, sm: 3 },
          py: 1,
          mt: "-4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          position: "relative",
          cursor: "text",
          overflow: "hidden",
          color: colors.ink,
          backgroundColor: "#f8fbff",
          border: error ? "1px solid #ff4d4f" : `1px solid ${colors.blue}`,
          borderTop: 0,
          borderRadius: "0 0 10px 10px",
          boxShadow: "0 18px 34px rgba(19, 39, 84, 0.14)",
          animation: error ? "shake 0.4s cubic-bezier(.36,.07,.19,.97) both" : "none",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.8 }}>
          PIN
        </Typography>

        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 3,
            color: error ? "#ff4d4f" : colors.ink,
            whiteSpace: "nowrap",
          }}
        >
          {renderPinText()}
        </Typography>

        <input
          ref={inputRef}
          type="tel"
          value={pin}
          onChange={handleChange}
          maxLength={4}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "text",
            border: "none",
            outline: "none",
          }}
          autoComplete="off"
        />
      </Paper>
    </Collapse>
  );
};

const ModuleCard = ({
  module,
  active,
  externalError,
  onClearError,
  onSelect,
  onValidatePin,
}) => {
  const Icon = module.icon;

  return (
    <Box sx={{ width: "100%" }}>
      <ButtonBase
        onClick={onSelect}
        sx={{
          width: "100%",
          height: 100,
          px: { xs: 2.2, sm: 3.6 },
          py: 1,
          display: "grid",
          gridTemplateColumns: "64px 2px 1fr 26px",
          alignItems: "center",
          columnGap: 3,
          textAlign: "left",
          backgroundColor: "#fff",
          border: active ? `1px solid ${colors.blue}` : "1px solid #dce5f2",
          borderRadius: active ? "10px 10px 0 0" : "10px",
          boxShadow: active
            ? "0 16px 34px rgba(0, 104, 255, 0.12)"
            : "0 14px 28px rgba(19, 39, 84, 0.10)",
          transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: "rgba(0,104,255,0.38)",
            boxShadow: "0 18px 34px rgba(19, 39, 84, 0.14)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            color: colors.blue,
            backgroundColor: "#edf4ff",
          }}
        >
          <Icon sx={{ fontSize: 38 }} />
        </Box>

        <Box
          sx={{
            width: 1,
            height: 42,
            backgroundColor: colors.line,
            display: { xs: "none", sm: "block" },
          }}
        />

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: colors.ink,
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1.15,
            }}
          >
            {module.title}
          </Typography>
          <Typography
            sx={{
              mt: 0.9,
              color: colors.muted,
              fontSize: 12.5,
              lineHeight: 1.25,
            }}
          >
            {module.description}
          </Typography>
        </Box>

        <ChevronRight
          sx={{
            justifySelf: "end",
            color: colors.blue,
            fontSize: 28,
            transform: active ? "rotate(90deg)" : "none",
            transition: "transform 180ms ease",
          }}
        />
      </ButtonBase>

      {module.protected && (
        <PinPanel
          visible={active}
          externalError={active ? externalError : false}
          onClearError={onClearError}
          onSubmit={(pin) => onValidatePin(module.key, pin)}
        />
      )}
    </Box>
  );
};

export default function ModuleSelector({
  user,
  onLogout,
  onSelectVentas,
  onValidatePin,
  onClearError,
  externalError,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const open = Boolean(anchorEl);
  const displayName = getDisplayName(user);

  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const handleSelectModule = (module) => {
    onClearError?.();

    if (!module.protected) {
      setActivePanel(null);
      onSelectVentas();
      return;
    }

    setActivePanel((prev) => (prev === module.key ? null : module.key));
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color: colors.ink,
      }}
    >
      <Box
        component="header"
        sx={{
          height: 60,
          px: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          zIndex: 20,
          backgroundColor: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderRadius: "0 0 10px 10px",
          boxShadow: "0 8px 22px rgba(15, 42, 89, 0.12)",
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 38,
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={vyontaLogoInk}
            alt="Vyonta"
            sx={{
              position: "absolute",
              width: 164,
              height: "auto",
              left: -33,
              top: -29,
              display: "block",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.8, sm: 1.4 } }}>
          <Box
            onClick={handleMenuClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.8, sm: 1.4 },
              cursor: "pointer",
              py: 0.5,
              pl: { xs: 0.5, sm: 1.2 },
              pr: 0.3,
              borderRadius: 999,
              transition: "background-color 160ms ease",
              "&:hover": { backgroundColor: "#f0f6ff" },
            }}
          >
            <Typography
              sx={{
                color: colors.ink,
                fontSize: { xs: 11.5, sm: 13 },
                fontWeight: 800,
                display: { xs: "none", sm: "block" },
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>

            <Avatar
              sx={{
                width: { xs: 34, sm: 38 },
                height: { xs: 34, sm: 38 },
                color: colors.blue,
                backgroundColor: "#f5f9ff",
                border: "1px solid #bed3ff",
                boxShadow: "0 4px 12px rgba(0, 104, 255, 0.10)",
              }}
            >
              <Person sx={{ fontSize: 20 }} />
            </Avatar>

            <KeyboardArrowDown sx={{ color: colors.blue, fontSize: 20 }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                border: "1px solid #dce5f2",
                borderRadius: "10px",
                boxShadow: "0 16px 32px rgba(19, 39, 84, 0.14)",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          height: "calc(100dvh - 62px)",
          position: "relative",
          zIndex: 1,
          px: 2,
          pt: 4,
          pb: 2,
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 700,
            mx: "auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              component="h1"
              sx={{
                color: colors.ink,
                fontSize: 30,
                fontWeight: 900,
                letterSpacing: "-0.9px",
              }}
            >
              Inicio
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 12 }}>
              Seleccioná el área con la que deseas trabajar.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {modules.map((module) => (
              <ModuleCard
                key={module.key}
                module={module}
                active={activePanel === module.key}
                externalError={externalError}
                onClearError={onClearError}
                onSelect={() => handleSelectModule(module)}
                onValidatePin={onValidatePin}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
